import { pageTitle } from '@palaccount/config';
import type { ItemCategoryResponse } from '@palaccount/contracts';
import { CATEGORY_COLOURS } from '@palaccount/contracts';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Ltr } from '@/components/amount';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AuthorshipLine } from '@/features/master-data/authorship-line';
import { QuickCreateSelect } from '@/features/quick-create/quick-create-select';
import { localizedName, localizedSymbol } from '@/lib/localized-name';
import { localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

import { useItemCategories, useItemCategoryMutations, useUnits } from './api';
import { InventoryTabs } from './inventory-tabs';

interface CategoryFormState {
  /** CAT-02: on = the server allocates CAT-### and the Code box is not sent. */
  codeAuto: boolean;
  code: string;
  name: string;
  /** S5 / CAT-04: the optional Arabic twin of `name`. */
  nameAr: string;
  colour: string;
  parentId: string;
  description: string;
  defaultUnitId: string;
  defaultTax: string;
  inventoryAccountCode: string;
}

const EMPTY: CategoryFormState = {
  codeAuto: true,
  code: '',
  name: '',
  nameAr: '',
  colour: '',
  parentId: '',
  description: '',
  defaultUnitId: '',
  defaultTax: '',
  inventoryAccountCode: '',
};

interface TreeRow {
  category: ItemCategoryResponse;
  depth: number;
}

/** MD-04 §4: the tree flattened depth-first, children under their parent. */
export function flattenTree(categories: readonly ItemCategoryResponse[]): TreeRow[] {
  const byParent = new Map<string | null, ItemCategoryResponse[]>();
  for (const category of categories) {
    const bucket = byParent.get(category.parentId) ?? [];
    bucket.push(category);
    byParent.set(category.parentId, bucket);
  }
  for (const bucket of byParent.values()) {
    bucket.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }
  const rows: TreeRow[] = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const category of byParent.get(parentId) ?? []) {
      rows.push({ category, depth });
      walk(category.id, depth + 1);
    }
  };
  walk(null, 0);
  // Orphans whose parent is filtered out (e.g. inactive parent hidden) still
  // must appear rather than vanish.
  const drawn = new Set(rows.map((row) => row.category.id));
  for (const category of categories) {
    if (!drawn.has(category.id)) rows.push({ category, depth: 0 });
  }
  return rows;
}

/**
 * MD-04 — the category tree: an indented list with item counts (inclusive of
 * descendants, field 9) and a simple form. + New pre-fills the parent with
 * the selected group (§6).
 */
export function CategoriesPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  // Active / Inactive / All (bugs: Item Categories) — a one-way "show
  // inactive" box could not show the inactive ones ON THEIR OWN.
  const [activeFilter, setActiveFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [editing, setEditing] = useState<ItemCategoryResponse | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [values, setValues] = useState<CategoryFormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('cats.title'));
  }, [t]);

  const { data: categories, isPending } = useItemCategories(activeFilter !== 'active');
  const { data: units } = useUnits();
  const { create, update, setActive } = useItemCategoryMutations();

  const rows = useMemo(
    () =>
      flattenTree(
        (categories ?? []).filter((category) =>
          activeFilter === 'inactive' ? !category.isActive : true,
        ),
      ),
    [categories, activeFilter],
  );

  const openNew = () => {
    setEditing(null);
    setValues(EMPTY);
    setError(null);
    setFormOpen(true);
  };
  const openEdit = (category: ItemCategoryResponse) => {
    setEditing(category);
    setValues({
      // An existing code is a typed value from here on — Auto is for NEW
      // groups only; a saved CAT-### can still be retyped.
      codeAuto: false,
      code: category.code,
      name: category.name,
      nameAr: category.nameAr ?? '',
      colour: category.colour ?? '',
      parentId: category.parentId ?? '',
      description: category.description ?? '',
      defaultUnitId: category.defaultUnitId ?? '',
      defaultTax: category.defaultTax ?? '',
      inventoryAccountCode: category.inventoryAccountCode ?? '',
    });
    setError(null);
    setFormOpen(true);
  };

  const submit = async () => {
    setError(null);
    if (values.name.trim() === '') return setError(t('cats.errName'));
    // CAT-02: with Auto on, the code is the server's to allocate — not a box
    // that can block the save.
    const codeAuto = !editing && values.codeAuto;
    if (!codeAuto && (values.code.trim() === '' || values.code.trim().length > 20)) {
      return setError(t('cats.errCode'));
    }
    const nameAr = values.nameAr.trim();
    try {
      if (editing) {
        await update.mutateAsync({
          categoryId: editing.id,
          body: {
            code: values.code.trim(),
            name: values.name.trim(),
            nameAr: nameAr === '' ? null : nameAr,
            colour: values.colour === '' ? null : values.colour,
            parentId: values.parentId === '' ? null : values.parentId,
            description: values.description.trim() === '' ? null : values.description.trim(),
            defaultUnitId: values.defaultUnitId === '' ? null : values.defaultUnitId,
            defaultTax: values.defaultTax.trim() === '' ? null : values.defaultTax.trim(),
            inventoryAccountCode:
              values.inventoryAccountCode.trim() === '' ? null : values.inventoryAccountCode.trim(),
          },
        });
      } else {
        await create.mutateAsync({
          ...(codeAuto ? {} : { code: values.code.trim() }),
          name: values.name.trim(),
          ...(nameAr === '' ? {} : { nameAr }),
          ...(values.colour === '' ? {} : { colour: values.colour }),
          ...(values.parentId === '' ? {} : { parentId: values.parentId }),
          sortOrder: 0,
          ...(values.description.trim() === '' ? {} : { description: values.description.trim() }),
          ...(values.defaultUnitId === '' ? {} : { defaultUnitId: values.defaultUnitId }),
          ...(values.defaultTax.trim() === '' ? {} : { defaultTax: values.defaultTax.trim() }),
          ...(values.inventoryAccountCode.trim() === ''
            ? {}
            : { inventoryAccountCode: values.inventoryAccountCode.trim() }),
        });
      }
      setFormOpen(false);
    } catch (cause) {
      // The cause rides along: a structured refusal (D46 — siblingTaken,
      // topLevelTaken, codeTaken, cycle) carries key+params, so the sentence
      // reaches an Arabic reader in Arabic.
      setError(
        localizeServerMessage(t, cause instanceof Error ? cause.message : String(cause), cause),
      );
    }
  };

  return (
    <div className="grid gap-4">
      <InventoryTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('cats.title')}</h2>
        <Gated permission="inventory.create" reasonKey="perm.cannot.createCategories">
          <Button onClick={openNew}>
            <Plus aria-hidden /> {t('common.new')}
          </Button>
        </Gated>
      </div>

      {formOpen ? (
        <Card>
          <CardHeader>
            <CardTitle>{t(editing ? 'cats.editTitle' : 'cats.newTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="grid gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="c-code">{t('cats.fCode')}</Label>
                  {/* CAT-02: the item form's own Auto box — on by default for
                      a NEW group, the server allocates CAT-### and the box
                      goes quiet. Editing keeps the typed code. */}
                  {editing ? null : (
                    <Label className="flex items-center gap-1.5 text-xs font-normal">
                      <Checkbox
                        checked={values.codeAuto}
                        onCheckedChange={(checked) =>
                          setValues({ ...values, codeAuto: checked === true })
                        }
                      />
                      {t('items.codeAuto')}
                    </Label>
                  )}
                </div>
                <Input
                  id="c-code"
                  dir="ltr"
                  maxLength={20}
                  className="font-mono"
                  disabled={!editing && values.codeAuto}
                  placeholder={!editing && values.codeAuto ? 'CAT-###' : undefined}
                  value={!editing && values.codeAuto ? '' : values.code}
                  onChange={(event) => setValues({ ...values, code: event.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="c-name">{t('cats.fName')}</Label>
                <Input
                  id="c-name"
                  maxLength={80}
                  value={values.name}
                  onChange={(event) => setValues({ ...values, name: event.target.value })}
                />
              </div>
              {/* S5 / CAT-04: the Arabic twin, beside the primary name. */}
              <div className="grid gap-1.5">
                <Label htmlFor="c-name-ar">{t('cats.fNameAr')}</Label>
                <Input
                  id="c-name-ar"
                  dir="rtl"
                  lang="ar"
                  maxLength={80}
                  value={values.nameAr}
                  onChange={(event) => setValues({ ...values, nameAr: event.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <span id="c-colour" className="text-sm font-medium">
                  {t('cats.fColour')}
                </span>
                {/* Field 1a: the six GEN-170 accents, drawn as the COLOURS they
                    are rather than as hex codes (bugs: Item Categories). A
                    colour picked off the custom control (CAT-03) joins them
                    as a seventh, selected swatch. */}
                <div role="radiogroup" aria-labelledby="c-colour" className="flex flex-wrap items-center gap-2">
                  {(
                    [
                      '',
                      ...CATEGORY_COLOURS,
                      ...(values.colour !== '' &&
                      !(CATEGORY_COLOURS as readonly string[]).includes(values.colour)
                        ? [values.colour]
                        : []),
                    ] as readonly string[]
                  ).map((colour) => {
                    const chosen = values.colour === colour;
                    return (
                      <button
                        key={colour || 'none'}
                        type="button"
                        role="radio"
                        aria-checked={chosen}
                        aria-label={colour === '' ? t('cats.noColour') : colour}
                        title={colour === '' ? t('cats.noColour') : colour}
                        onClick={() => setValues({ ...values, colour })}
                        className={cn(
                          'flex size-8 items-center justify-center rounded-full border-2 text-xs text-muted-foreground transition',
                          chosen ? 'border-foreground ring-2 ring-ring/50' : 'border-transparent',
                          colour === '' && 'border-dashed border-input',
                        )}
                        style={colour === '' ? undefined : { backgroundColor: colour }}
                      >
                        {colour === '' ? '—' : null}
                      </button>
                    );
                  })}
                  {/* CAT-03: any #rrggbb — the native picker, labelled. */}
                  <input
                    id="c-colour-custom"
                    type="color"
                    aria-label={t('cats.customColour')}
                    title={t('cats.customColour')}
                    className="size-8 cursor-pointer rounded-full border-2 border-dashed border-input bg-transparent p-0.5"
                    value={values.colour === '' ? '#0e9aa1' : values.colour}
                    onChange={(event) =>
                      setValues({ ...values, colour: event.target.value.toLowerCase() })
                    }
                  />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="c-parent">{t('cats.fParent')}</Label>
                <NativeSelect
                  id="c-parent"
                  value={values.parentId}
                  onChange={(event) => setValues({ ...values, parentId: event.target.value })}
                >
                  <option value="">{t('cats.fParentNone')}</option>
                  {(categories ?? [])
                    .filter((category) => category.id !== editing?.id)
                    .map((category) => (
                      <option key={category.id} value={category.id}>
                        {localizedName(category, lang)}
                      </option>
                    ))}
                </NativeSelect>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="c-unit">{t('cats.fDefaultUnit')}</Label>
                {/* GEN-51 (D26). The PARENT picker two fields above is
                    deliberately left alone: it picks a category, and this is
                    the category screen — it already carries a New button, so
                    a create row there would be a second door into the same
                    room. A UNIT is a different record and has no door here. */}
                <QuickCreateSelect
                  entity="unit"
                  id="c-unit"
                  value={values.defaultUnitId}
                  onChange={(next) => setValues({ ...values, defaultUnitId: next })}
                >
                  <option value="">{t('cats.fDefaultUnitNone')}</option>
                  {(units ?? []).map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {localizedName(unit, lang)} ({localizedSymbol(unit, lang)})
                    </option>
                  ))}
                </QuickCreateSelect>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="c-tax">{t('cats.fDefaultTax')}</Label>
                <Input
                  id="c-tax"
                  maxLength={60}
                  value={values.defaultTax}
                  onChange={(event) => setValues({ ...values, defaultTax: event.target.value })}
                />
              </div>
              {/* INV-08 §12's per-category inventory mapping: the account
                  CODE items filed here post their stock value to when they
                  carry none of their own. Empty = 1131 Raw Materials. */}
              <div className="grid gap-1.5">
                <Label htmlFor="c-inventory-account">{t('cats.fInventoryAccount')}</Label>
                <Input
                  id="c-inventory-account"
                  dir="ltr"
                  maxLength={20}
                  value={values.inventoryAccountCode}
                  onChange={(event) =>
                    setValues({ ...values, inventoryAccountCode: event.target.value })
                  }
                />
              </div>
              <div className="grid gap-1.5 sm:col-span-3">
                <Label htmlFor="c-description">{t('cats.fDescription')}</Label>
                <Input
                  id="c-description"
                  maxLength={400}
                  value={values.description}
                  onChange={(event) => setValues({ ...values, description: event.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Gated
                permission={editing ? 'inventory.edit' : 'inventory.create'}
                reasonKey={editing ? 'perm.cannot.editCategories' : 'perm.cannot.createCategories'}
              >
                <Button onClick={() => void submit()} disabled={!values.name.trim()}>
                  {t('common.save')}
                </Button>
              </Gated>
              <Button variant="outline" onClick={() => setFormOpen(false)}>
                {t('common.cancel')}
              </Button>
              {editing ? (
                <Gated permission="inventory.edit" reasonKey="perm.cannot.editCategories">
                <Button
                  variant="outline"
                  onClick={() => {
                    setActive
                      .mutateAsync({ categoryId: editing.id, active: !editing.isActive })
                      .then(() => setFormOpen(false))
                      .catch((cause: unknown) =>
                        setError(
                          localizeServerMessage(
                            t,
                            cause instanceof Error ? cause.message : String(cause),
                            cause,
                          ),
                        ),
                      );
                  }}
                >
                  {t(editing.isActive ? 'items.switchOff' : 'items.switchOn')}
                </Button>
                </Gated>
              ) : null}
            </div>
            {editing ? <AuthorshipLine record={editing} /> : null}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="grid gap-4">
          <NativeSelect
            aria-label={t('common.activeFilterLabel')}
            className="w-44"
            value={activeFilter}
            onChange={(event) =>
              setActiveFilter(event.target.value as 'active' | 'inactive' | 'all')
            }
          >
            <option value="active">{t('common.active')}</option>
            <option value="inactive">{t('common.inactive')}</option>
            <option value="all">{t('common.activeFilterAll')}</option>
          </NativeSelect>
          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : rows.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">{t('cats.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('cats.colName')}</TableHead>
                  <TableHead>{t('cats.colCode')}</TableHead>
                  <TableHead className="text-end">{t('cats.colItems')}</TableHead>
                  <TableHead>{t('cats.colStatus')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(({ category, depth }) => (
                  <TableRow
                    key={category.id}
                    className="cursor-pointer"
                    onClick={() => openEdit(category)}
                  >
                    <TableCell>
                      <span
                        className="flex items-center gap-2"
                        style={{ paddingInlineStart: `${depth * 1.25}rem` }}
                      >
                        {category.colour ? (
                          <span
                            aria-hidden
                            className="size-2.5 rounded-full"
                            style={{ backgroundColor: category.colour }}
                          />
                        ) : null}
                        <span className="font-medium">{localizedName(category, lang)}</span>
                      </span>
                    </TableCell>
                    <TableCell>
                      <Ltr value={category.code} className="font-mono text-muted-foreground" />
                    </TableCell>
                    <TableCell className="text-end font-mono">{category.itemCount}</TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {t(category.isActive ? 'common.active' : 'common.inactive')}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
